import posthog from 'posthog-js';
import { biographyData } from '../data/biography.js';
import Chatbot from './chatbot.js';

// Configuration
const CONFIG = {
  API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
  DEBUG: import.meta.env.VITE_DEBUG === 'true',
  ENVIRONMENT: import.meta.env.VITE_ENVIRONMENT || 'production',
  RECAPTCHA_SITE_KEY: import.meta.env.VITE_RECAPTCHA_V3_SITE_KEY || null,
  POSTHOG_TOKEN: (typeof __POSTHOG_TOKEN__ !== 'undefined' && __POSTHOG_TOKEN__) ? __POSTHOG_TOKEN__ : (import.meta.env.VITE_POSTHOG_TOKEN || null),
  CALENDAR_LINK: import.meta.env.VITE_CALENDAR_INVITE_LINK || null
};

function initPostHog() {
  if (!CONFIG.POSTHOG_TOKEN) return;
  posthog.init(CONFIG.POSTHOG_TOKEN, {
    api_host: 'https://t.omarsaad.no',
    defaults: '2026-01-30',
    person_profiles: 'identified_only',
  });
}

// Utility functions
const utils = {
  // DOM helpers
  $(selector) {
    return document.querySelector(selector);
  },
  
  $$(selector) {
    return document.querySelectorAll(selector);
  },
  
  // API helpers
  async fetchAPI(endpoint, options = {}) {
    const url = `${CONFIG.API_BASE_URL}${endpoint}`;
    const defaultOptions = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      }
    };
    
    try {
      const response = await fetch(url, { ...defaultOptions, ...options });
      const data = await response.json();

      if (!response.ok) {
        const error = new Error(data.detail?.message || data.message || `HTTP ${response.status}`);
        error.status = response.status;
        error.errorCode = data.detail?.error;
        throw error;
      }

      return data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  },
  
};

// Navigation functionality
class Navigation {
  constructor() {
    this.header = utils.$('#header');
    this.navToggle = utils.$('#nav-toggle');
    this.navMenu = utils.$('#nav-menu');
    this.navLinks = utils.$$('.nav__link');
    this.sections = utils.$$('section[id]');
    this._scrollPending = false;

    this.init();
  }
  
  init() {
    this.bindEvents();
    this.handleScroll();
  }
  
  bindEvents() {
    // Mobile menu toggle
    this.navToggle?.addEventListener('click', () => this.toggleMobileMenu());
    
    // Smooth scroll for nav links
    this.navLinks.forEach(link => {
      link.addEventListener('click', (e) => this.handleNavClick(e));
    });
    
    // Close mobile menu when clicking outside
    document.addEventListener('click', (e) => {
      if (!this.navMenu?.contains(e.target) && !this.navToggle?.contains(e.target)) {
        this.closeMobileMenu();
      }
    });

    // Close mobile menu on Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.navMenu?.classList.contains('nav__menu--open')) {
        this.closeMobileMenu();
        this.navToggle?.focus();
      }
    });
    
    // Handle scroll for header styling — throttled to one call per animation frame
    window.addEventListener('scroll', () => {
      if (this._scrollPending) return;
      this._scrollPending = true;
      requestAnimationFrame(() => {
        this.handleScroll();
        this._scrollPending = false;
      });
    });
    
    // Handle resize
    window.addEventListener('resize', () => this.handleResize());
  }
  
  toggleMobileMenu() {
    const isOpen = this.navMenu?.classList.toggle('nav__menu--open');
    this.navToggle?.classList.toggle('nav__toggle--open');
    this.navToggle?.setAttribute('aria-expanded', String(!!isOpen));
  }

  closeMobileMenu() {
    this.navMenu?.classList.remove('nav__menu--open');
    this.navToggle?.classList.remove('nav__toggle--open');
    this.navToggle?.setAttribute('aria-expanded', 'false');
  }
  
  handleNavClick(e) {
    e.preventDefault();
    const href = e.target.getAttribute('href');
    posthog.capture('nav_link_clicked', { target: href });

    if (href.startsWith('#')) {
      const target = utils.$(href);
      if (target) {
        const headerHeight = this.header?.offsetHeight || 80;
        const targetPosition = target.offsetTop - headerHeight;

        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth'
        });

        this.closeMobileMenu();
      }
    }
  }
  
  handleScroll() {
    if (!this.header) return;
    
    const scrolled = window.scrollY > 50;
    this.header.classList.toggle('header--scrolled', scrolled);
    
    // Update active nav link
    this.updateActiveNavLink();
  }
  
  updateActiveNavLink() {
    const scrollPos = window.scrollY + (this.header?.offsetHeight || 80) + 50;

    this.sections.forEach(section => {
      const sectionTop = section.offsetTop;
      const sectionHeight = section.offsetHeight;
      const sectionId = section.getAttribute('id');
      const navLink = utils.$(`a[href="#${sectionId}"]`);

      if (scrollPos >= sectionTop && scrollPos < sectionTop + sectionHeight) {
        this.navLinks.forEach(link => link.classList.remove('nav__link--active'));
        navLink?.classList.add('nav__link--active');
      }
    });
  }
  
  handleResize() {
    if (window.innerWidth > 768) {
      this.closeMobileMenu();
    }
  }
}

// Contact form functionality
class ContactForm {
  constructor() {
    this.form = utils.$('#contact-form');
    this.recaptchaSiteKey = CONFIG.RECAPTCHA_SITE_KEY;
    this.init();
  }

  init() {
    this.bindEvents();
    if (this.recaptchaSiteKey) {
      this.loadRecaptcha();
    }
  }

  loadRecaptcha() {
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${this.recaptchaSiteKey}`;
    script.async = true;
    document.head.appendChild(script);
  }

  async getRecaptchaToken() {
    if (!this.recaptchaSiteKey || typeof grecaptcha === 'undefined') return null;
    return new Promise(resolve => {
      grecaptcha.ready(() => {
        grecaptcha.execute(this.recaptchaSiteKey, { action: 'contact' })
          .then(resolve)
          .catch(() => resolve(null));
      });
    });
  }
  
  bindEvents() {
    this.form?.addEventListener('submit', (e) => this.handleSubmit(e));
  }
  
  async handleSubmit(e) {
    e.preventDefault();
    
    const formData = new FormData(this.form);
    const data = Object.fromEntries(formData.entries());
    data.mailing_list_signup = formData.get('mailing_list_signup') === 'on';

    // Basic validation
    if (!this.validateForm(data)) {
      return;
    }
    
    // Show loading state
    const submitBtn = this.form?.querySelector('button[type="submit"]');
    const originalText = submitBtn?.textContent;
    if (submitBtn) {
      submitBtn.textContent = 'Sending...';
      submitBtn.disabled = true;
    }
    
    posthog.capture('contact_form_submitted', { subject: data.subject });
    if (data.mailing_list_signup) {
      posthog.capture('mailing_list_opt_in_checked', { source: 'contact_form' });
    }
    try {
      const recaptchaToken = await this.getRecaptchaToken();
      const response = await utils.fetchAPI('/contact/send', {
        method: 'POST',
        body: JSON.stringify({ ...data, recaptcha_token: recaptchaToken })
      });

      if (response?.success) {
        posthog.identify(data.email, { name: data.name, email: data.email, subject: data.subject });
        posthog.capture('contact_form_sent', { name: data.name, email: data.email, subject: data.subject });
        posthog.capture('contact_form_succeeded');
        if (data.mailing_list_signup) {
          posthog.capture('mailing_list_subscribed', { source: 'contact_form' });
        }
        this.showSuccess("Thank you for your message! I'll get back to you soon.");
        this.form?.reset();
      } else {
        posthog.capture('contact_form_failed', { reason: 'server_rejection' });
        this.showError(response?.message || 'Sorry, there was an error sending your message. Please try again.');
      }

    } catch (error) {
      if (error.errorCode === 'rate_limited') {
        posthog.capture('contact_form_failed', { reason: 'rate_limited' });
        this.showError(error.message);
      } else {
        posthog.capture('contact_form_failed', { reason: 'network_error' });
        this.showError(error.message || 'Sorry, there was an error sending your message. Please try again.');
      }
    } finally {
      // Reset button
      if (submitBtn) {
        submitBtn.textContent = originalText;
        submitBtn.disabled = false;
      }
    }
  }
  
  validateForm(data) {
    const { name, email, subject, message } = data;
    
    if (!name || !email || !subject || !message) {
      this.showError('Please fill in all required fields.');
      return false;
    }
    
    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      this.showError('Please enter a valid email address.');
      return false;
    }
    
    return true;
  }
  
  showSuccess(message) {
    this.showNotification(message, 'success');
  }
  
  showError(message) {
    this.showNotification(message, 'error');
  }
  
  showNotification(message, type) {
    // Create notification element
    const notification = document.createElement('div');
    notification.className = `notification notification--${type}`;
    notification.textContent = message;
    
    // Add to page
    document.body.appendChild(notification);
    
    // Show with animation
    setTimeout(() => notification.classList.add('notification--show'), 100);
    
    // Remove after delay
    setTimeout(() => {
      notification.classList.remove('notification--show');
      setTimeout(() => notification.remove(), 300);
    }, 5000);
  }
}

const _GITHUB_SVG = `<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"/></svg>`;
const _LIVE_SVG = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>`;

// Data loader for dynamic content
class DataLoader {
  constructor() {
    this.init();
  }
  
  async init() {
    try {
      this.loadBiographyData();
    } catch (error) {
      console.error('Failed to load dynamic content:', error);
    }
  }
  
  loadBiographyData() {
    try {
      this.renderProjects(biographyData.projects);
    } catch (error) {
      console.error('Failed to load biography data:', error);
    }
  }

  renderProjects(projects) {
    const grid = utils.$('#projects-grid');
    if (!grid || !Array.isArray(projects)) return;
    grid.innerHTML = '';
    projects.forEach(p => grid.appendChild(this._createProjectCard(p)));
  }

  _createProjectCard(p) {
    const article = document.createElement('article');
    article.className = 'project-card';

    const title = document.createElement('h3');
    title.className = 'project-card__title';
    title.textContent = p.title;

    const desc = document.createElement('p');
    desc.className = 'project-card__description';
    desc.textContent = p.description;

    const tech = document.createElement('div');
    tech.className = 'project-card__tech';
    p.tech.forEach(t => {
      const tag = document.createElement('span');
      tag.className = 'project-card__tag';
      tag.textContent = t;
      tech.appendChild(tag);
    });

    article.append(title, desc, tech, this._createProjectLinks(p));
    return article;
  }

  _createProjectLinks(p) {
    const div = document.createElement('div');
    div.className = 'project-card__links';

    if (!p.github && !p.live) {
      const span = document.createElement('span');
      span.className = 'project-card__private';
      span.textContent = 'Private / internal project';
      div.appendChild(span);
      return div;
    }

    if (p.github) div.appendChild(this._createExternalLink(p.github, 'GitHub', _GITHUB_SVG, p.title, 'github'));
    if (p.live) div.appendChild(this._createExternalLink(p.live, 'Live', _LIVE_SVG, p.title, 'live'));
    return div;
  }

  _createExternalLink(href, label, svgHtml, projectTitle, linkType) {
    const a = document.createElement('a');
    a.href = href;
    a.className = 'project-card__link';
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.innerHTML = `${svgHtml} ${label}`;
    if (projectTitle && linkType) {
      a.addEventListener('click', () => posthog.capture('project_link_clicked', { project_title: projectTitle, link_type: linkType }));
    }
    return a;
  }
}

// Book-a-Call confirmation modal
class BookCallModal {
  constructor(calendarUrl) {
    this.calendarUrl = calendarUrl;
    this.dialog = utils.$('#book-call-dialog');
    this._bindTriggers();
    this._bindDialog();
  }

  _bindTriggers() {
    ['hero-book-call', 'contact-book-call'].forEach(id => {
      const btn = utils.$(`#${id}`);
      if (!btn) return;
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });
  }

  _bindDialog() {
    utils.$('#book-call-confirm')?.addEventListener('click', () => this.confirm());
    utils.$('#book-call-cancel')?.addEventListener('click', () => this.close());
    this.dialog?.addEventListener('click', (e) => {
      if (e.target === this.dialog) this.close();
    });
  }

  open() {
    posthog.capture('book_call_modal_opened');
    this.dialog?.showModal();
  }

  confirm() {
    posthog.capture('book_call_confirmed');
    window.open(this.calendarUrl, '_blank', 'noopener,noreferrer');
    this.close();
  }

  close() {
    this.dialog?.close();
  }
}

// Mailing list signup modal
class MailingListModal {
  constructor() {
    this.dialog = utils.$('#mailing-list-dialog');
    this.form = utils.$('#mailing-list-form');
    this.emailInput = utils.$('#mailing-list-email');
    this.statusEl = utils.$('#mailing-list-status');
    this._bindTriggers();
    this._bindDialog();
  }

  _bindTriggers() {
    utils.$('#footer-mailing-list-open')?.addEventListener('click', () => this.open());
  }

  _bindDialog() {
    utils.$('#mailing-list-cancel')?.addEventListener('click', () => this.close());
    this.dialog?.addEventListener('click', (e) => {
      if (e.target === this.dialog) this.close();
    });
    this.form?.addEventListener('submit', (e) => this.handleSubmit(e));
  }

  open() {
    posthog.capture('mailing_list_modal_opened');
    this._setStatus('');
    this.dialog?.showModal();
  }

  close() {
    this.dialog?.close();
  }

  async handleSubmit(e) {
    e.preventDefault();
    const email = this.emailInput?.value.trim();
    if (!email) return;

    const submitBtn = utils.$('#mailing-list-submit');
    const originalText = submitBtn?.textContent;
    if (submitBtn) {
      submitBtn.textContent = 'Subscribing...';
      submitBtn.disabled = true;
    }

    posthog.capture('mailing_list_modal_submitted');
    try {
      const response = await utils.fetchAPI('/mailing-list/subscribe', {
        method: 'POST',
        body: JSON.stringify({ email })
      });

      if (response?.success) {
        posthog.identify(email, { email });
        posthog.capture('mailing_list_subscribed', { source: 'modal' });
        this._setStatus("You're subscribed! Thanks for joining.");
        this.form?.reset();
      } else {
        posthog.capture('mailing_list_modal_failed', { reason: 'server_rejection' });
        this._setStatus('Sorry, something went wrong. Please try again.');
      }
    } catch (error) {
      posthog.capture('mailing_list_modal_failed', { reason: 'network_error' });
      this._setStatus(error.message || 'Sorry, something went wrong. Please try again.');
    } finally {
      if (submitBtn) {
        submitBtn.textContent = originalText;
        submitBtn.disabled = false;
      }
    }
  }

  _setStatus(message) {
    if (this.statusEl) this.statusEl.textContent = message;
  }
}

// Analytics event bindings
class Analytics {
  constructor() {
    this._viewedSections = new Set();
    this._bindHeroCtas();
    this._bindSocialLinks();
    this._initSectionObserver();
  }

  _bindHeroCtas() {
    const ctaMap = { '#chat': 'chat_now', '#about': 'learn_more', '#contact': 'contact_me' };
    utils.$$('.hero__actions a').forEach(a => {
      if (a.id === 'hero-book-call') return; // modal handles its own tracking
      const cta = ctaMap[a.getAttribute('href')];
      if (cta) {
        a.addEventListener('click', () => posthog.capture('hero_cta_clicked', { cta }));
      }
    });
  }

  _bindSocialLinks() {
    const platform = el => el.getAttribute('aria-label')?.toLowerCase();
    utils.$$('.hero__social-link').forEach(a => {
      a.addEventListener('click', () => posthog.capture('social_link_clicked', { platform: platform(a), location: 'hero' }));
    });
    utils.$$('.footer__social-link').forEach(a => {
      a.addEventListener('click', () => posthog.capture('social_link_clicked', { platform: platform(a), location: 'footer' }));
    });
  }

  _initSectionObserver() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const section = entry.target.getAttribute('id');
        if (this._viewedSections.has(section)) return;
        this._viewedSections.add(section);
        posthog.capture('section_viewed', { section });
      });
    }, { threshold: 0.3 });

    ['about', 'projects', 'chat', 'contact'].forEach(id => {
      const el = utils.$(`#${id}`);
      if (el) observer.observe(el);
    });
  }
}

// Initialize application when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  if (CONFIG.DEBUG) {
    console.log('Omar\'s Landing Page - Debug Mode');
    console.log('Config:', CONFIG);
  }

  initPostHog();
  posthog.capture('pageview');

  // Initialize components
  new Navigation();
  new Chatbot({ apiBaseUrl: CONFIG.API_BASE_URL, onEvent: posthog.capture.bind(posthog) });
  new ContactForm();
  new DataLoader();
  new Analytics();
  new MailingListModal();

  // Wire calendar booking modal from env var
  if (CONFIG.CALENDAR_LINK) {
    new BookCallModal(CONFIG.CALENDAR_LINK);
  } else {
    ['hero-book-call', 'contact-book-call'].forEach(id => {
      const el = utils.$(`#${id}`);
      if (el) el.style.display = 'none';
    });
  }

  // Add loaded class to body for CSS animations
  document.body.classList.add('loaded');
});

